import { genreOptions, regionOptions, yearOptions } from "@/mocks/titles";
import { TitleFilters } from "@/utils/content";

interface CategoryFiltersProps {
  filters: TitleFilters;
  onChange: (next: TitleFilters) => void;
  onClear: () => void;
  activeCount: number;
  tags: string[];
}

const ratingOptions = [
  { label: "全部评分", value: 0 },
  { label: "8.5 分以上", value: 8.5 },
  { label: "8.0 分以上", value: 8 },
  { label: "7.5 分以上", value: 7.5 },
];

const chipClass = (active: boolean) =>
  `cursor-pointer whitespace-nowrap rounded-md border px-2.5 py-1.5 text-xs transition-colors duration-200 ${
    active
      ? "border-primary-900 bg-primary-900 text-background-50"
      : "border-background-300 bg-background-50 text-foreground-600 hover:border-accent-400 hover:text-accent-700"
  }`;

export default function CategoryFilters({
  filters,
  onChange,
  onClear,
  activeCount,
  tags,
}: CategoryFiltersProps) {
  const setField = (key: keyof TitleFilters, value: string | number | undefined) => {
    onChange({ ...filters, [key]: value });
  };

  const groupTitle = (title: string) => (
    <div className="mb-2.5 flex items-center justify-between">
      <p className="font-label text-[11px] font-semibold uppercase tracking-[0.18em] text-foreground-500">
        {title}
      </p>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="font-heading text-sm font-semibold text-foreground-950">筛选条件</p>
        <button
          type="button"
          onClick={onClear}
          className="cursor-pointer whitespace-nowrap text-xs text-foreground-500 transition-colors duration-200 hover:text-accent-700 disabled:opacity-40"
          disabled={activeCount === 0}
        >
          清除全部
        </button>
      </div>

      <div>
        {groupTitle("地区")}
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setField("region", "")} className={chipClass(!filters.region)}>
            全部
          </button>
          {regionOptions.map((region) => (
            <button
              key={region}
              type="button"
              onClick={() => setField("region", region)}
              className={chipClass(filters.region === region)}
            >
              {region}
            </button>
          ))}
        </div>
      </div>

      <div>
        {groupTitle("年份")}
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setField("year", "")} className={chipClass(!filters.year)}>
            全部
          </button>
          {yearOptions.map((year) => (
            <button
              key={year}
              type="button"
              onClick={() => setField("year", String(year))}
              className={chipClass(filters.year === String(year))}
            >
              {year}
            </button>
          ))}
        </div>
      </div>

      <div>
        {groupTitle("类型")}
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setField("genre", "")} className={chipClass(!filters.genre)}>
            全部
          </button>
          {genreOptions.map((genre) => (
            <button
              key={genre}
              type="button"
              onClick={() => setField("genre", genre)}
              className={chipClass(filters.genre === genre)}
            >
              {genre}
            </button>
          ))}
        </div>
      </div>

      <div>
        {groupTitle("评分")}
        <div className="flex flex-wrap gap-2">
          {ratingOptions.map((option) => (
            <button
              key={option.label}
              type="button"
              onClick={() => setField("minRating", option.value || undefined)}
              className={chipClass((filters.minRating || 0) === option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        {groupTitle("热门标签")}
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setField("tag", "")} className={chipClass(!filters.tag)}>
            全部
          </button>
          {tags.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => setField("tag", tag)}
              className={chipClass(filters.tag === tag)}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg bg-secondary-50 p-3.5">
        <p className="flex items-center gap-1.5 text-xs font-medium text-secondary-900">
          <i className="ri-information-line" />
          演示说明
        </p>
        <p className="mt-1.5 text-[11px] leading-relaxed text-secondary-800/85">
          筛选与排序均为前端实时演示，结果来自站内示例数据。真实的数据匹配将在后续接入内容接口后实现。
        </p>
      </div>
    </div>
  );
}